/**
 * Patched Tus plugin to fix "Cannot mark a queued request as done: this indicates a bug" error.
 *
 * Issue: When uploading 40+ files from 3rd party sources via Companion, the socket can complete (success/error)
 * before the rate-limited queuedRequest actually starts running. Calling .done() on a queuedRequest (not yet
 * running/completed) request throws an error.
 *
 * Fix: Track whether queuedRequest has started via `queuedRequestStarted` flag. On socket completion:
 *   - If request started: call .done() to mark it complete
 *   - If still queued: call .abort() to remove from queue
 *
 * This patch is needed until we upgrade to Uppy 3.x where this bug is fixed.
 */

import Tus, { TusOptions } from '@uppy/tus'
import { Socket } from '@uppy/companion-client'
import EventTracker from '@uppy/utils/lib/EventTracker'
import getSocketHost from '@uppy/utils/lib/getSocketHost'
import emitSocketProgress from '@uppy/utils/lib/emitSocketProgress'
import { RateLimitedQueue } from '@uppy/utils/lib/RateLimitedQueue'

interface TusPatchedPlugin {
  opts: TusOptions
  requests: RateLimitedQueue
  uploaderSockets: any
  uploaderEvents: any

  onCancelAll: (fileID: string, eventHandler: (arg?: Record<string, any>) => void) => void
  onFileRemove: (fileID: string, cb: (arg: string) => void) => void
  onPause: (fileID: string, cb: (arg: string) => void) => void
  onPauseAll: (fileID: string, cb: () => void) => void
  onResumeAll: (fileID: string, cb: () => void) => void
  onRetry: (fileID: string, cb: () => void) => void
  onRetryAll: (fileID: string, cb: () => void) => void
  resetUploaderReferences: (fileID: string, opts?: Record<string, any>) => void
}

class TusPatchedPlugin extends Tus {
  constructor(uppy, opts) {
    super(uppy, opts)
  }

  /**
   * See the comment on the upload() method.
   *
   * Additionally, when an upload is removed, completed, or cancelled, we need to close the WebSocket connection. This is
   * handled by the resetUploaderReferences() function, so the same guidelines apply as in upload().
   *
   * @param {UppyFile} file
   */
  connectToServerSocket(file) {
    return new Promise((resolve, reject) => {
      const token = file.serverToken
      const host = getSocketHost(file.remote.companionUrl)
      const socket = new Socket({ target: `${host}/api/${token}` })
      this.uploaderSockets[file.id] = socket
      this.uploaderEvents[file.id] = new EventTracker(this.uppy)

      let queuedRequest
      let queuedRequestStarted = false  // Track if request started running

      this.onFileRemove(file.id, () => {
        socket.send('cancel', {})
        queuedRequest.abort()
        this.resetUploaderReferences(file.id)
        resolve(`upload ${file.id} was removed`)
      })

      this.onPause(file.id, (isPaused) => {
        if (isPaused) {
          // Remove this file from the queue so another file can start in its place.
          socket.send('pause', {})
          queuedRequest.abort()
        } else {
          // Resuming an upload should be queued, else you could pause and then
          // resume a queued upload to make it skip the queue.
          queuedRequest.abort()
          queuedRequest = this.requests.run(() => {
            socket.send('resume', {})
            return () => { }
          })
        }
      })

      this.onPauseAll(file.id, () => {
        socket.send('pause', {})
        queuedRequest.abort()
      })

      this.onCancelAll(file.id, ({ reason } = {}) => {
        if (reason === 'user') {
          socket.send('cancel', {})
          queuedRequest.abort()
          this.resetUploaderReferences(file.id)
        }
        resolve(`upload ${file.id} was canceled`)
      })

      this.onResumeAll(file.id, () => {
        queuedRequest.abort()
        if (file.error) {
          socket.send('pause', {})
        }
        queuedRequest = this.requests.run(() => {
          socket.send('resume', {})
          return () => { }
        })
      })

      this.onRetry(file.id, () => {
        // Only do the retry if the upload is actually in progress;
        // else we could try to send these messages when the upload is still queued.
        // We may need a better check for this since the socket may also be closed
        // for other reasons, like network failures.
        if (socket.isOpen) {
          socket.send('pause', {})
          socket.send('resume', {})
        }
      })

      this.onRetryAll(file.id, () => {
        // See the comment in the onRetry() call
        if (socket.isOpen) {
          socket.send('pause', {})
          socket.send('resume', {})
        }
      })

      socket.on('progress', (progressData) => emitSocketProgress(this, progressData, file))

      socket.on('error', (errData) => {
        const { message } = errData.error
        const error = Object.assign(new Error(message), { cause: errData.error })

        // If the remote retry optimisation should not be used,
        // close the socket—this will tell companion to clear state and delete the file.
        if (!this.opts.useFastRemoteRetry) {
          this.resetUploaderReferences(file.id)
          // Remove the serverToken so that a new one will be created for the retry.
          this.uppy.setFileState(file.id, {
            serverToken: null,
          })
        } else {
          socket.close()
        }

        this.uppy.emit('upload-error', file, error)
        // Only call done() if the request actually started
        if (queuedRequest) {
          if (queuedRequestStarted) {
            queuedRequest.done()
          } else {
            queuedRequest.abort()  // Remove from queue if not started
          }
        }
        reject(error)
      })

      socket.on('success', (data) => {
        const uploadResp = {
          uploadURL: data.url,
        }

        this.uppy.emit('upload-success', file, uploadResp)
        this.resetUploaderReferences(file.id)
        // Only call done() if the request actually started
        if (queuedRequest) {
          if (queuedRequestStarted) {
            queuedRequest.done()
          } else {
            queuedRequest.abort()  // Remove from queue if not started
          }
        }
        socket.close()

        // @ts-ignore
        resolve()
      })

      queuedRequest = this.requests.run(() => {
        queuedRequestStarted = true  // Mark as started
        if (file.isPaused) {
          socket.send('pause', {})
        }

        // Don't do anything here, the caller will take care of cancelling the upload itself
        // using resetUploaderReferences(). This is because resetUploaderReferences() has to be
        // called when this request is still in the queue, and has not been started yet, too. At
        // that point this cancellation function is not going to be called.
        // Also, we need to remove the request from the queue _without_ destroying everything
        // related to this upload to handle pauses.
        return () => {}
      })
    })
  }
}

export default TusPatchedPlugin
